"use client";

import { useState, useRef, useEffect } from "react";
import {
  Settings,
  LogOut,
  Briefcase,
  Building2,
  Users,
  ChevronRight,
  PlusCircle,
  CheckCircle2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { cn } from "@/lib/utils";

export function UserNav() {
  const { user, signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (!user) return null;

  // Format initials and display name
  const displayName =
    user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.name || user.email.split("@")[0];

  const initials =
    user.first_name && user.last_name
      ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
      : user.name
        ? user.name.substring(0, 2).toUpperCase()
        : user.email[0].toUpperCase();

  const profiles = [
    {
      name: "Investor",
      id: user.investor_id,
      href: "/investor-profile",
      icon: Briefcase,
      color: "text-blue-500",
    },
    {
      name: "Realtor",
      id: user.realtor_id,
      href: "/realtor-profile",
      icon: Users,
      color: "text-purple-500",
    },
    {
      name: "Property Manager",
      id: user.property_manager_id,
      href: "/property-manager-profile",
      icon: Building2,
      color: "text-green-500",
    },
  ];

  return (
    <div className="relative" ref={menuRef}>
      <Button
        variant="ghost"
        className="relative h-10 w-10 rounded-full bg-muted p-0 hover:bg-muted/80 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="User menu"
      >
        <div className="flex h-full w-full items-center justify-center rounded-full border-2 border-background bg-primary/10 text-primary transition-colors hover:bg-primary/20">
          <span className="text-sm font-bold uppercase tracking-tighter">
            {initials}
          </span>
        </div>
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-72 max-w-[calc(100vw-2rem)] origin-top-right overflow-hidden rounded-xl border bg-card text-card-foreground shadow-2xl ring-1 ring-black/5 animate-in fade-in zoom-in duration-200">
          {/* User Header */}
          <div className="flex items-center gap-3 border-b bg-muted/30 px-4 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <span className="text-xs font-bold">{initials}</span>
            </div>
            <div className="flex flex-col overflow-hidden">
              <span className="truncate text-sm font-semibold tracking-tight">
                {displayName}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                {user.email}
              </span>
            </div>
          </div>

          <div className="p-2">
            {/* Profiles Section */}
            <div className="mb-1 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
              Profiles
            </div>
            <div className="space-y-1">
              {profiles.map((profile) => (
                <Link
                  key={profile.name}
                  href={profile.href}
                  onClick={() => setIsOpen(false)}
                  className="group flex items-center justify-between rounded-lg p-2 text-sm transition-all hover:bg-accent hover:text-accent-foreground"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        "rounded-md p-1.5 bg-background border shadow-sm transition-transform group-hover:scale-110",
                        profile.color,
                      )}
                    >
                      <profile.icon className="h-4 w-4" />
                    </div>
                    <span className="font-medium">{profile.name}</span>
                  </div>
                  {profile.id ? (
                    <CheckCircle2 className="h-4 w-4 text-primary opacity-80" />
                  ) : (
                    <PlusCircle className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary/60 transition-colors" />
                  )}
                </Link>
              ))}
            </div>

            <div className="my-2 border-t border-muted" />

            {/* Settings & Logout */}
            <div className="space-y-1">
              <Link
                href="/settings"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 rounded-lg p-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <div className="rounded-md p-1.5 bg-background border shadow-sm">
                  <Settings className="h-4 w-4" />
                </div>
                <span>Account Settings</span>
                <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground/50" />
              </Link>

              <button
                onClick={() => {
                  setIsOpen(false);
                  signOut();
                }}
                className="flex w-full items-center gap-3 rounded-lg p-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
              >
                <div className="rounded-md p-1.5 bg-background border border-destructive/20 shadow-sm text-destructive">
                  <LogOut className="h-4 w-4" />
                </div>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
